/**
 * API functions for registrant bulk payment.
 * Uses the same client as generated SDK; endpoints are getBulkPaymentFees,
 * createBulkPendingPaymentRecords, makeBulkLicensePayment.
 */

import { client } from "@/hera-api/client/client.gen";
import type { QuestionResponseSchema } from "@/hera-api/client/types.gen";
import { getApiErrorMessage } from "@/lib/utils";

export interface LicenseFeesForPeriodSchema {
  start_date: string;
  end_date: string | null;
  registration_fee: string;
  late_fee: string;
  already_paid_fee?: string;
  original_registration_fee?: string | null;
  accrued_late_fees?: Array<string | number>;
}

export interface BulkPaymentFeesItem {
  license_id: string;
  license_type_id: string;
  license_type_display_order: number;
  display_id: string;
  property_address: string;
  license_type_name: string;
  jurisdiction_name: string;
  fees: LicenseFeesForPeriodSchema[];
  inspection_fee: string | null;
  check_processing_fee: string | null;
  answers_to_additional_questions?: Record<string, unknown>;
  fees_payment_screen_questions?: QuestionResponseSchema[];
  /** From API: usable global invoice template exists for this license type. */
  invoice_template_available?: boolean;
  ams_company_name?: string | null;
}

export interface BulkPaymentFeesResponse {
  items: BulkPaymentFeesItem[];
  /** Persisted runtime cache rows recomputed for this request (parallel, per license). */
  runtime_cache_recalculated?: number;
}

export interface LicensePeriodCreationParams {
  selected: boolean;
  start_date: string;
  end_date: string | null;
  fee: string;
  late_fee: string;
  prefilled_fee?: string;
  prefilled_late_fee?: string;
}

export interface BulkPendingPaymentItem {
  license_id: string;
  periods: LicensePeriodCreationParams[];
  inspection_fee?: string;
  inspection_stripe_fee?: string;
}

export interface CreateBulkPendingPaymentsRequest {
  items: BulkPendingPaymentItem[];
  excluded_license_ids?: string[];
}

export interface CreateBulkInvoicePaymentRequest
  extends CreateBulkPendingPaymentsRequest {
  ams_company_name?: string | null;
}

export interface PayBulkLicenseRequest {
  payment_success_url: string;
  payment_cancel_url: string;
  payment_method_type?: "card" | "us_bank_account";
  license_ids?: string[];
}

export interface PayLicenseResponse {
  checkout_session_id: string;
  checkout_session_url: string;
}

export interface CreateBulkCheckPaymentResponse {
  manual_transaction_id: string;
  check_id: string;
  check_mailing_address: string | null;
}

export async function createBulkCheckPayment(
  body: CreateBulkPendingPaymentsRequest,
): Promise<CreateBulkCheckPaymentResponse> {
  const result = await client.post<CreateBulkCheckPaymentResponse>({
    url: "/registrant/bulk-check-payment",
    body,
    parseAs: "json",
    security: [{ scheme: "bearer", type: "http" }],
  });
  if (result.error) {
    throw new Error(
      getApiErrorMessage(result.error, "Failed to create bulk check payment"),
    );
  }
  return result.data as CreateBulkCheckPaymentResponse;
}

export async function createBulkInvoicePayment(
  body: CreateBulkInvoicePaymentRequest,
): Promise<CreateBulkCheckPaymentResponse> {
  const result = await client.post<CreateBulkCheckPaymentResponse>({
    url: "/registrant/bulk-invoice-payment",
    body,
    parseAs: "json",
    security: [{ scheme: "bearer", type: "http" }],
  });
  if (result.error) {
    throw new Error(
      getApiErrorMessage(result.error, "Failed to create bulk invoice payment"),
    );
  }
  return result.data as CreateBulkCheckPaymentResponse;
}

/**
 * Registrant dashboard global filters carried into the bulk payment screen. The
 * server applies them before recomputing fees, so a filtered dashboard loads a
 * smaller (and faster) payment screen.
 */
export interface BulkPaymentFeesFilters {
  jurisdictionDisplayCodes?: string[];
  licenseTypeIds?: string[];
  registrantAccountId?: string | null;
  amsCompanyName?: string | null;
  /**
   * Company question the dashboard matched `amsCompanyName` against. Sent so the
   * server filters on the same question, which matters when an account has more
   * than one AMS-style question.
   */
  amsQuestionId?: string | null;
}

export function bulkPaymentFeesQuery(
  filters: BulkPaymentFeesFilters | undefined,
): Record<string, string | string[]> {
  const query: Record<string, string | string[]> = {};
  if (filters?.jurisdictionDisplayCodes?.length) {
    query.jurisdiction_display_codes = filters.jurisdictionDisplayCodes;
  }
  if (filters?.licenseTypeIds?.length) {
    query.license_type_ids = filters.licenseTypeIds;
  }
  if (filters?.registrantAccountId) {
    query.registrant_account_id = filters.registrantAccountId;
  }
  if (filters?.amsCompanyName) {
    query.ams_company_name = filters.amsCompanyName;
    if (filters.amsQuestionId) {
      query.ams_question_id = filters.amsQuestionId;
    }
  }
  return query;
}

export async function getBulkPaymentFees(
  filters?: BulkPaymentFeesFilters,
): Promise<BulkPaymentFeesResponse> {
  const result = await client.get<BulkPaymentFeesResponse>({
    url: "/registrant/bulk-payment-fees",
    query: bulkPaymentFeesQuery(filters),
    parseAs: "json",
    security: [{ scheme: "bearer", type: "http" }],
  });
  if (result.error) {
    throw new Error(
      getApiErrorMessage(result.error, "Failed to fetch bulk payment fees"),
    );
  }
  return result.data as BulkPaymentFeesResponse;
}

export async function createBulkPendingPaymentRecords(
  body: CreateBulkPendingPaymentsRequest,
): Promise<void> {
  const result = await client.post({
    url: "/registrant/bulk-pending-payments",
    body,
    parseAs: "text",
    security: [{ scheme: "bearer", type: "http" }],
  });
  if (result.error) {
    throw new Error(
      getApiErrorMessage(result.error, "Failed to create pending payments"),
    );
  }
}

export async function makeBulkLicensePayment(
  body: PayBulkLicenseRequest,
): Promise<PayLicenseResponse> {
  const result = await client.post<PayLicenseResponse>({
    url: "/registrant/bulk-payment",
    body,
    parseAs: "json",
    security: [{ scheme: "bearer", type: "http" }],
  });
  if (result.error) {
    throw new Error(
      getApiErrorMessage(result.error, "Failed to create payment session"),
    );
  }
  return result.data as PayLicenseResponse;
}

export async function abandonStripeCheckoutSession(
  checkoutSessionId: string,
): Promise<void> {
  const result = await client.post({
    url: `/registrant/stripe/checkout_sessions/${checkoutSessionId}/abandon`,
    parseAs: "text",
    security: [{ scheme: "bearer", type: "http" }],
  });
  if (result.error) {
    throw new Error(
      getApiErrorMessage(result.error, "Failed to abandon checkout session"),
    );
  }
}
